import 'symbol-observable';
import '@eti/validation';
import '@eti/debug';
import '@eti/utils';
import * as Sentry from '@sentry/react';
import './common/debug/components/DebugSiteProperties';

import ReactDOM from 'react-dom';
import App from './main/components/App';
import suppressConsoleWarnings from './utils/suppressConsoleWarnings';
import { initSubSession } from './subSession';
import { isMultiSessionEnabled } from './subSession/isMultiSessionEnabled';
import { isDev } from './common/debug/utils';

declare const window: Window & { spa_variables: any };

if (window.spa_variables?.properties?.['IbeClient.SentrySDK.Enabled'] ?? false) {
  const getEnvironment = () => {
    if (isDev) {
      return 'development';
    }

    return window.location.host.includes('ibetest.etgcloud.io') ? 'test' : 'production';
  };

  const getRedactedUrl = (urlString: string) => {
    try {
      const url = new URL(urlString);

      url.searchParams.forEach((value, key) => {
        url.searchParams.set(key, '[REDACTED]');
      });

      return url.toString();
    } catch {
      return urlString;
    }
  };

  Sentry.init({
    dsn: window.spa_variables?.properties?.['IbeClient.SentrySDK.DSN'] ?? '',
    sendDefaultPii: false,
    enableLogs: true,
    environment: getEnvironment(),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ['error', 'warn'],
      }),
      Sentry.graphqlClientIntegration({
        endpoints: [/^\/graphql\//gi],
      }),
    ],
    sampleRate: 0.15,
    tracesSampleRate: 0.15,
    beforeBreadcrumb: (breadcrumb) => {
      if (breadcrumb.category === 'fetch' && breadcrumb.data?.url?.startsWith('/ibemgr')) {
        return null;
      }

      if (breadcrumb.category === 'navigation') {
        return {
          ...breadcrumb,
          data: {
            ...breadcrumb.data,
            from: getRedactedUrl(breadcrumb.data?.from ?? ''),
            to: getRedactedUrl(breadcrumb.data?.to ?? ''),
          },
        };
      }

      return breadcrumb;
    },
    beforeSend: (event) => {
      // event.request is genuinely absent for most captured errors (anything not
      // tied to an HTTP request) — the original unguarded access would throw here,
      // which Sentry's SDK handles by silently dropping the event instead of
      // sending it, so return the event unchanged when there's no request to redact.
      if (!event.request) {
        return event;
      }

      return {
        ...event,
        request: {
          ...event.request,
          url: getRedactedUrl(event.request.url ?? ''),
        },
      };
    },
  });
}

if (process.env.NODE_ENV === 'development') {
  suppressConsoleWarnings();
}

// Runs before React mounts, so the property comes straight off spa_variables.
initSubSession(isMultiSessionEnabled());

ReactDOM.render(<App />, document.getElementById('root'));
